"use client";

import { useEffect, useRef } from "react";

const reasons = [
  {
    number: "01",
    title: "Adaptabilidad Total",
    description: "Cada proyecto es único. Nos adaptamos a tus necesidades, presupuesto y plazos para entregar exactamente lo que imaginas.",
    icon: "◈",
  },
  {
    number: "02",
    title: "Precisión Técnica",
    description: "Tolerancias de hasta 0.05mm. Materiales premium: PLA, PETG, ABS y resina. Calidad de fabricación profesional en cada pieza.",
    icon: "◎",
  },
  {
    number: "03",
    title: "Soluciones a Medida",
    description: "Desde el boceto hasta la pieza final. Diseñamos, modelamos e imprimimos según tus especificaciones exactas.",
    icon: "◇",
  },
  {
    number: "04",
    title: "Experiencia Local",
    description: "Estudio en Tenerife, Canarias. Conocemos el mercado canario y ofrecemos atención personalizada y cercana.",
    icon: "◉",
  },
];

const hospitalityItems = [
  { icon: "🍽", label: "Porta-menús y soportes de carta personalizados" },
  { icon: "🏷", label: "Señalética y rótulos de mesa con tu marca" },
  { icon: "🧂", label: "Accesorios de mesa: servilleteros, especieros, etc." },
  { icon: "🎨", label: "Elementos decorativos y ambientación 3D" },
  { icon: "📦", label: "Packaging y presentación de producto" },
  { icon: "✨", label: "Piezas de branding corporativo para el local" },
];

export default function WhyEvolviaSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    const items = ref?.current?.querySelectorAll(".reveal");
    items?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-28 px-6 lg:px-10 bg-evolvia-bg-light relative overflow-hidden">
      {/* Geometric accent lines */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-evolvia-copper/20 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-evolvia-copper/20 to-transparent" />
      {/* Background dot grid */}
      <div className="absolute inset-0 dot-grid-light opacity-60" />
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div className="reveal">
            <span className="text-xs font-700 uppercase tracking-[0.25em] text-evolvia-copper mb-3 block">
              Por qué elegirnos
            </span>
            <h2 className="text-5xl md:text-6xl font-black leading-[0.92] tracking-tight text-evolvia-text-dark">
              ¿Por qué
              <br />
              <span className="gradient-text-copper">Evolvia 3D?</span>
            </h2>
          </div>
          <p className="reveal delay-200 text-gray-600 max-w-sm leading-relaxed">
            La adaptabilidad y las soluciones personalizadas son el núcleo de todo lo que hacemos. No hay dos proyectos iguales.
          </p>
        </div>

        {/* Reasons grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {reasons?.map((reason, i) => (
            <div
              key={reason?.number}
              className={`reveal delay-${(i + 1) * 100} service-card-light p-8 group`}
            >
              {/* Icon + Number */}
              <div className="flex items-start justify-between mb-6">
                <span className="text-3xl text-evolvia-copper">{reason?.icon}</span>
                <span className="text-xs font-800 text-gray-300 tracking-widest">{reason?.number}</span>
              </div>

              {/* Copper accent line */}
              <div className="w-8 h-0.5 bg-evolvia-copper mb-5 group-hover:w-16 transition-all duration-500" />

              <h3 className="text-xl font-700 text-evolvia-text-dark mb-3 leading-tight">
                {reason?.title}
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed">
                {reason?.description}
              </p>
            </div>
          ))}
        </div>

        {/* Hostelería & Restauración Sector Highlight */}
        <div className="reveal mt-16 service-card-light p-10 lg:p-14 group overflow-hidden relative">
          {/* Copper top border accent */}
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-evolvia-copper via-evolvia-copper/60 to-transparent" />

          <div className="flex flex-col lg:flex-row lg:items-start gap-10">
            {/* Left: text block */}
            <div className="lg:w-2/5 flex-shrink-0">
              <span className="inline-flex items-center gap-2 text-xs font-700 uppercase tracking-[0.2em] text-evolvia-copper mb-4">
                <span className="text-base">🍴</span> Sector destacado
              </span>
              <h3 className="text-3xl md:text-4xl font-black leading-tight text-evolvia-text-dark mb-4">
                Hostelería &amp;
                <br />
                <span className="gradient-text-copper">Restauración</span>
              </h3>
              <div className="w-10 h-0.5 bg-evolvia-copper mb-5 group-hover:w-20 transition-all duration-500" />
              <p className="text-gray-600 leading-relaxed mb-6">
                En Evolvia 3D sabemos que cada restaurante, bar o hotel tiene una identidad única. Creamos piezas 3D personalizadas que refuerzan tu imagen de marca, mejoran la experiencia del cliente y diferencian tu local de la competencia.
              </p>
              <p className="text-sm text-gray-500 leading-relaxed">
                Desde soportes de carta hasta elementos decorativos exclusivos — todo diseñado y fabricado en Tenerife, con tiempos de entrega ágiles y atención directa.
              </p>
            </div>

            {/* Right: items grid */}
            <div className="lg:w-3/5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {hospitalityItems?.map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-4 p-5 rounded-sm border border-gray-200 hover:border-evolvia-copper/40 bg-white/60 hover:bg-white transition-all duration-300"
                >
                  <span className="text-2xl flex-shrink-0 mt-0.5">{item?.icon}</span>
                  <span className="text-sm text-gray-600 leading-snug font-500">{item?.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
